/**
 * PendingSignOffStrip — "this batch is waiting for your signature", with the signature attached.
 *
 * ux-production-supervisor #1: the counter-signature was actionable on exactly one page, and that
 * page was in no menu and nothing linked to it. This strip puts the same act on the batch — so the
 * monitor's "Open batch", the Gantt's bar click and the cockpit's link all now end somewhere the
 * supervisor can finish the job — and it names the *right* step, because it reads the approval rows
 * rather than whatever operation happens to be running (#5).
 */
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Space, Tag, Typography } from 'antd';
import { PenLine } from 'lucide-react';
import { get_supervisor_approvals } from '@/utils/get_apis';
import SupervisorSignOffControl, { waitingHours, waitingLabel } from '@/components/SupervisorSignOffControl';

const { Text } = Typography;

export default function PendingSignOffStrip({ batchNumber, onChange, style }) {
  const [rows, setRows] = useState([]);

  const load = useCallback(() => {
    if (!batchNumber) return;
    get_supervisor_approvals(
      (res) => {
        const data = res?.data?.status === 'success' ? res.data.data || [] : [];
        setRows(data.filter((r) => String(r.status || '').toUpperCase() === 'PENDING'));
      },
      { batch_number: batchNumber, status: 'PENDING' }
    );
  }, [batchNumber]);

  useEffect(() => {
    load();
  }, [load]);

  if (rows.length === 0) return null;
  const oldest = rows.reduce((h, r) => Math.max(h, waitingHours(r) || 0), 0);

  return (
    <Alert
      type={oldest > 24 ? 'error' : 'warning'}
      showIcon
      icon={<PenLine size={16} />}
      style={{ marginBottom: 12, ...style }}
      message={
        <span>
          <b>{rows.length}</b> step{rows.length === 1 ? '' : 's'} on this batch {rows.length === 1 ? 'is' : 'are'} finished and
          waiting for a supervisor signature{oldest > 0 ? ` — the oldest for ${waitingLabel(rows.reduce((a, b) => ((waitingHours(a) || 0) > (waitingHours(b) || 0) ? a : b)))}` : ''}.
        </span>
      }
      description={
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 6 }}>
          {rows.map((r) => (
            <div key={r.approval_id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
              <Space size={6} wrap style={{ flex: 1, minWidth: 240 }}>
                <Tag color="blue">#{r.operation?.number ?? '—'}</Tag>
                <b>{r.operation?.name || '—'}</b>
                <Text type="secondary" style={{ fontSize: 12 }}>
                  finished by {r.requested_by || 'an operator'} · waiting {waitingLabel(r)}
                </Text>
              </Space>
              <SupervisorSignOffControl
                approval={r}
                onReviewed={() => {
                  load();
                  onChange?.();
                }}
              />
            </div>
          ))}
        </div>
      }
    />
  );
}

PendingSignOffStrip.propTypes = {
  batchNumber: PropTypes.string,
  onChange: PropTypes.func,
  style: PropTypes.object,
};
